import { useCallback, useMemo } from 'react';
import type { ComponentProps } from 'react';
import { Checkbox } from '@cbhq/cds-web/controls/Checkbox';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { Pressable } from '@cbhq/cds-web/system/Pressable';
import { Text } from '@cbhq/cds-web/typography/Text';

import type { ProductIconName } from './ProductIcon';
import { ProductIcon } from './ProductIcon';

type TriggerAccessibilityProps = Partial<ComponentProps<typeof Pressable>>;

// Mirrors CDS Checkbox states. The LE row is never fully "checked" itself;
// nested product selection renders as `indeterminate`, otherwise `none`.
export type LEProductAccessRowSelectionState = 'none' | 'indeterminate';

export type LEProductAccessRowParams = {
  entityId: string;
  selectionState: LEProductAccessRowSelectionState;
  onSelectionToggle: (selected: boolean) => void;
  isExpanded: boolean;
  onExpandToggle: () => void;
  triggerAccessibilityProps?: TriggerAccessibilityProps;
  iconName: ProductIconName;
  iconBackground: string;
  title: string;
  description?: string;
  isEditable?: boolean;
};

export function LEProductAccessRow({
  entityId,
  selectionState,
  onSelectionToggle,
  isExpanded,
  onExpandToggle,
  triggerAccessibilityProps,
  iconName,
  iconBackground,
  title,
  description,
  isEditable = true,
}: LEProductAccessRowParams) {
  const isIndeterminate = selectionState === 'indeterminate';

  const caretIconStyle = useMemo(
    () => ({
      transition: 'transform 200ms ease-in-out',
      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
    }),
    [isExpanded],
  );

  const handleChange = useCallback(() => {
    onSelectionToggle(!isIndeterminate);
  }, [isIndeterminate, onSelectionToggle]);

  return (
    <HStack flexGrow={1} alignItems="center" gap={2} width="100%">
      <Checkbox
        value={entityId}
        accessibilityLabel={title}
        checked={false}
        indeterminate={isIndeterminate}
        onChange={handleChange}
        disabled={!isEditable}
        controlSize={22}
        borderRadius={0}
        background={isIndeterminate ? 'fg' : undefined}
        borderColor={isIndeterminate ? 'fg' : undefined}
      />
      <Pressable
        {...triggerAccessibilityProps}
        onClick={onExpandToggle}
        background="transparent"
        noScaleOnPress
        block
      >
        <HStack alignItems="center" gap={1.5} width="100%">
          <ProductIcon name={iconName} background={iconBackground} />
          <VStack gap={0} alignItems="flex-start" flexGrow={1}>
            <Text as="span" font="label1" color="fg" noWrap>
              {title}
            </Text>
            {description ? (
              <Text as="span" font="label2" color="fgMuted" noWrap>
                {description}
              </Text>
            ) : null}
          </VStack>
          <Icon name="caretDown" size="s" color="fgMuted" style={caretIconStyle} />
        </HStack>
      </Pressable>
    </HStack>
  );
}
